import { useCallback, useEffect, useState } from "react";
import { Alert, Box, Button, Card, CardContent, Chip, Pagination, Stack, TextField, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { createExpense, getExpenseCategories, getMoneyOut, getMoneyOutSummary } from "../api/finances";
import { getApiError } from "../api/errors";
import { useAuth } from "../context/AuthContext";
import MainLayout from "../layouts/MainLayout";

const today = () => {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};
const money = (value) => Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const blankExpense = () => ({ category: "", description: "", amount: "", expense_date: today(), payment_method: "cash", reference: "", notes: "", idempotency_key: window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}` });

export default function PaymentsExpenses() {
  const { t } = useTranslation();
  const { role, selectedStore, selectedWorkspace } = useAuth();
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState("all");
  const [records, setRecords] = useState({ supplier_payments: [], operating_expenses: [], total_supplier_payments: 0, total_operating_expenses: 0 });
  const [summary, setSummary] = useState(null);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(blankExpense);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    try {
      const [list, categoryList] = await Promise.all([
        getMoneyOut({ page, page_size: 20, kind: filter }), getExpenseCategories(),
      ]);
      setRecords(list.data);
      setCategories(categoryList.data || []);
      if (role === "owner") {
        const totals = await getMoneyOutSummary();
        setSummary(totals.data);
      } else {
        setSummary(null);
      }
    } catch (requestError) {
      setError(getApiError(requestError, t("finance.loadError")));
    }
  }, [filter, page, role, t]);

  useEffect(() => { load(); }, [load, selectedStore?.public_id, selectedWorkspace?.public_id]);

  const save = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await createExpense(form);
      setForm(blankExpense());
      setOpen(false);
      setNotice(t("finance.saveSuccess"));
      await load();
    } catch (requestError) {
      setError(getApiError(requestError, t("finance.saveError")));
    } finally {
      setBusy(false);
    }
  };

  const supplierItems = (records.supplier_payments || []).map((item) => ({ ...item, kind: "supplier", date: item.payment_date }));
  const expenseItems = (records.operating_expenses || []).map((item) => ({ ...item, kind: "expense", date: item.expense_date }));
  const visible = (filter === "supplier" ? supplierItems : filter === "expense" ? expenseItems : [...supplierItems, ...expenseItems])
    .sort((a, b) => b.date.localeCompare(a.date) || b.created_at.localeCompare(a.created_at));
  const count = filter === "supplier" ? records.total_supplier_payments || 0
    : filter === "expense" ? records.total_operating_expenses || 0
    : (records.total_supplier_payments || 0) + (records.total_operating_expenses || 0);

  return <MainLayout>
    <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" gap={2} mb={2}>
      <Box><Typography variant="h4" fontWeight={900}>{t("finance.title")}</Typography><Typography color="text.secondary">{t("finance.subtitle", { store: selectedStore?.name || t("finance.thisStore") })}</Typography></Box>
      <Button variant="contained" onClick={() => setOpen((current) => !current)}>{t("finance.recordExpense")}</Button>
    </Stack>
    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
    {notice && <Alert severity="success" sx={{ mb: 2 }}>{notice}</Alert>}
    {role === "owner" && summary && <Box component="section" aria-label={t("finance.moneyOutSummary")} sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" }, gap: 1.5, mb: 2 }}>
      {[
        [t("finance.supplierPayments"), summary.supplier_payments],
        [t("finance.operatingExpenses"), summary.operating_expenses],
        [t("finance.totalMoneyOut"), summary.total_money_out],
      ].map(([label, amount]) => (
        <Card key={label} component="article" aria-label={label} sx={{ height: "100%", border: "1px solid", borderColor: "divider" }}>
          <CardContent sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary" fontWeight={700}>{label}</Typography>
            <Typography variant="h5" fontWeight={900} sx={{ mt: 1, overflowWrap: "anywhere" }}>{money(amount)}</Typography>
          </CardContent>
        </Card>
      ))}
    </Box>}
    {open && <Card sx={{ mb: 2 }}><CardContent><Box component="form" onSubmit={save}>
      <Typography variant="h6" fontWeight={800} mb={2}>{t("finance.paidExpense")}</Typography>
      <Stack direction={{ xs: "column", md: "row" }} gap={1.5} mb={1.5}>
        <TextField required label={t("finance.category")} value={form.category} inputProps={{ list: "expense-category-options" }} onChange={(event) => setForm({ ...form, category: event.target.value })} />
        <Box component="datalist" id="expense-category-options">{categories.map((category) => <option key={category} value={category} />)}</Box>
        <TextField required label={t("finance.description")} value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} />
        <TextField required label={t("common.amount")} type="number" inputProps={{ min: 0.01, step: "0.01" }} value={form.amount} onChange={(event) => setForm({ ...form, amount: event.target.value })} />
        <TextField required label={t("finance.expenseDate")} type="date" slotProps={{ inputLabel: { shrink: true } }} value={form.expense_date} onChange={(event) => setForm({ ...form, expense_date: event.target.value })} />
      </Stack>
      <Stack direction={{ xs: "column", md: "row" }} gap={1.5} mb={1.5}>
        <TextField required label={t("finance.paymentMethod")} value={form.payment_method} onChange={(event) => setForm({ ...form, payment_method: event.target.value })} />
        <TextField label={t("finance.referenceReceipt")} value={form.reference} onChange={(event) => setForm({ ...form, reference: event.target.value })} />
        <TextField label={t("common.notes")} value={form.notes} onChange={(event) => setForm({ ...form, notes: event.target.value })} />
      </Stack>
      <Button type="submit" variant="contained" disabled={busy}>{t("finance.saveExpense")}</Button>
    </Box></CardContent></Card>}
    <Stack direction="row" gap={1} mb={2} flexWrap="wrap">
      {[ ["all", "finance.all"], ["supplier", "finance.supplierPayments"], ["expense", "finance.operatingExpensesTab"] ].map(([key, label]) =>
        <Chip key={key} label={t(label)} onClick={() => { setPage(1); setFilter(key); }} color={filter === key ? "primary" : "default"} variant={filter === key ? "filled" : "outlined"} />)}
    </Stack>
    <Stack gap={1.5}>{visible.map((item) => <Card key={`${item.kind}-${item.public_id}`} component="article" aria-label={item.kind === "supplier" ? t("finance.supplierPaymentTo", { name: item.supplier_name }) : t("finance.expenseLabel", { description: item.description })}><CardContent sx={{ p: 2 }}>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 1fr) auto" }, columnGap: 3, rowGap: 1.5 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary">{item.kind === "supplier" ? t("finance.supplierPayment") : item.category}</Typography>
          <Typography fontWeight={800} sx={{ overflowWrap: "anywhere" }}>{item.kind === "supplier" ? item.supplier_name : item.description}</Typography>
        </Box>
        <Box sx={{ minWidth: 0, textAlign: { sm: "right" } }}>
          <Typography variant="caption" color="text.secondary">{t("common.amount")}</Typography>
          <Typography fontWeight={900}>{money(item.amount)}</Typography>
        </Box>
      </Box>
      <Stack direction="row" gap={1.5} flexWrap="wrap" sx={{ mt: 1.5, pt: 1.5, borderTop: "1px solid", borderColor: "divider" }}>
        <Typography variant="body2" color="text.secondary">{t("finance.dateLabel", { value: item.date })}</Typography>
        <Typography variant="body2" color="text.secondary">{t("finance.methodLabel", { value: item.kind === "supplier" ? item.method : item.payment_method })}</Typography>
        {item.reference && <Typography variant="body2" color="text.secondary">{t("finance.referenceLabel", { value: item.reference })}</Typography>}
      </Stack>
    </CardContent></Card>)}</Stack>
    {visible.length === 0 && <Typography color="text.secondary">{t("finance.noRecords")}</Typography>}
    {count > 20 && <Pagination sx={{ mt: 2 }} page={page} count={Math.ceil(count / 20)} onChange={(_, next) => setPage(next)} />}
  </MainLayout>;
}
